import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { API_URL } from '../../config';
import { getValidImageUrl } from '../../utils/getImageUrl';
import Spinner from '../../components/Spinner';

const MyEAs = () => {
  const { token } = useContext(AuthContext);
  const [eas, setEas] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEAs = async () => {
      try {
        const res = await fetch(`${API_URL}/api/user/eas`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setEas(data);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    if (token) fetchEAs();
  }, [token]);

  const handleDownload = async (productId, productName) => {
    try {
      const res = await fetch(`${API_URL}/api/downloads/${productId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Download failed');
      
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${productName.replace(/\\s+/g, '_')}_v1.0.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      alert('Failed to securely download file.');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-[24px] font-bold text-white mb-1">My Purchased Expert Advisors</h1>
        <p className="text-[14px] text-on-surface-variant">
          Access your digital asset licenses, setup parameters, and active software packages.
        </p>
      </div>

      {loading ? (
        <div className="py-20 flex justify-center">
          <Spinner text="Verifying digital assets..." />
        </div>
      ) : eas.length === 0 ? (
        <div className="bg-surface-container border border-white/5 rounded-3xl p-16 text-center flex flex-col items-center justify-center shadow-lg">
          <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mb-6 border border-white/10">
            <span className="material-symbols-outlined text-[32px] text-primary">smart_toy</span>
          </div>
          
          <h2 className="text-[20px] font-bold text-white mb-2">No EAs Purchased Yet</h2>
          <p className="text-[14px] text-on-surface-variant mb-8 max-w-md">
            You don't have any active EA software downloads linked to your account yet. Shop our collections to begin.
          </p>
          
          <Link to="/marketplace" className="bg-primary text-[#131418] font-bold px-8 py-3 rounded-full text-[14px] hover:shadow-[0_0_15px_rgba(240,185,11,0.4)] hover:-translate-y-0.5 transition-all duration-300 flex items-center gap-2">
            Browse Marketplace <span className="material-symbols-outlined text-[18px]">arrow_right_alt</span>
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {eas.map(ea => (
            <div key={ea.id} className="bg-surface-container border border-white/5 rounded-3xl overflow-hidden shadow-lg flex flex-col">
              <div className="h-48 overflow-hidden relative group">
                <img src={ea.image ? getValidImageUrl(ea.image) : ''} alt={ea.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#131418] to-transparent opacity-80" />
                <div className="absolute top-4 right-4 bg-green-500 text-white text-[10px] font-bold px-2 py-1 rounded uppercase tracking-wider">
                  Active License
                </div>
              </div>
              <div className="p-6 flex-1 flex flex-col">
                <h3 className="text-[18px] font-bold text-white mb-1">{ea.name}</h3>
                <p className="text-primary text-[12px] font-bold tracking-widest uppercase mb-4">{ea.platform}</p>
                <div className="flex-1" />
                <button 
                  onClick={() => handleDownload(ea.id, ea.name)}
                  className="w-full bg-primary/10 text-primary border border-primary/30 font-bold py-3 rounded-lg text-[14px] hover:bg-primary hover:text-[#131418] transition-all flex items-center justify-center gap-2 mt-4"
                >
                  <span className="material-symbols-outlined text-[18px]">download</span>
                  Download Package
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyEAs;
